import WebViewer from "@pdftron/webviewer"

const licenseKey = import.meta.env.VITE_WEBVIEWER_KEY

// Boot the single shared Apryse instance for the Quotations flow into `container`, loading
// `fileLink`. Returns the WebViewer promise; the caller wires up the resolved instance (panels,
// shadow styles, event listeners) — this owns only the constructor + its stability options.
//
// It deliberately loads NO document. `initialDoc` used to carry the presigned URL, but the
// orchestrator's own load effect then fetched the same file again and replaced it with a Blob — the
// PDF was measured arriving from S3 TWICE on every first open, and parsed twice with it. It also
// made the cache in core/documentBlobCache unreachable on the open that matters most, since
// `initialDoc` can only take a URL and so always went to the network. The orchestrator is now the
// single loader; its effect fires as soon as the instance is initialized.
export const initWebViewer = (container: HTMLElement) => {
	// Clear any stale web component out of the host before constructing (#1430). `UI.dispose()` does
	// NOT remove the `<apryse-webviewer>` element it created, so a second init into the same host
	// STACKS a second one below the first — the pop-out window opens straight into editor mode, so
	// React StrictMode's double-invoke runs init twice on the same host. No-op on the common path.
	container.querySelectorAll("apryse-webviewer").forEach((stale) => stale.remove())

	return WebViewer(
		{
			licenseKey,
			path: "/webviewer",
			css: "/webviewer-quotation.css",
			// Selecting an annotation must not move the viewport. Apryse's default is to focus the
			// notes panel's input for the newly selected annotation, and focusing it scrolls that
			// annotation into view in the FIRST viewer — measured as a single direct scrollTop write,
			// no DocumentViewer API involved, landing on the annotation's page top at unchanged zoom.
			// That is what dragged the pane the user was reading onto the counterpart's page whenever
			// smart navigation added its counterpart to the selection (#1311): there is one selection
			// and it lives in the primary manager, so a counterpart meant for the OTHER pane still
			// enters this viewer's selection set. This app has no notes panel, so the focus behaviour
			// has nothing to offer and only this side effect.
			autoFocusNoteOnAnnotationSelection: false,
			// Production stability options
			enableOptimizedWorkers: true,
			fullAPI: false,
			useDownloader: false,
			// Spin up the PDF wasm worker alongside the UI instead of waiting for the first
			// document to ask for it. Measured ~380 ms off a cold boot (interleaved A/B, 8 runs);
			// the two phases overlap instead of running back to back.
			preloadWorker: WebViewer.WorkerTypes.PDF,
		},
		container,
	)
}
